// Per-allergen data loading + decode.
//
// Each allergen ships as a set of PNG-L strips, each holding several vertically
// stacked SLICE_W x SLICE_H slices. We decode each slice as a SLICE_W x SLICE_H
// createImageBitmap crop with colorSpaceConversion/premultiplyAlpha = 'none'
// (the proven P4 integrity path) and pack the RED channel of each slice into
// one contiguous Uint8Array of N_SLICES*SLICE_W*SLICE_H bytes, laid out
// layer-by-layer (slice 0 first) ready for a single texSubImage3D-per-layer
// upload into an N_SLICES-layer R8 TEXTURE_2D_ARRAY.
//
// The slice geometry (N_SLICES, SLICES_PER_STRIP, SLICE_W, SLICE_H) is read
// DYNAMICALLY from the manifest via setSliceLayout(), called once from
// loadManifest() before any decode or texture allocation. The values are
// exported as `let` so consumers (./pollenLayer reads N_SLICES/SLICE_* as ES
// live bindings) automatically observe the manifest-driven values. Defaults
// below match the historical 36-slice dekadal layout for safety if a manifest
// without an encoding block is ever loaded.
//
// Decode is lazy-per-allergen (on first selection) and the resulting CPU
// Uint8 copy stays resident, so re-selecting an allergen never re-decodes and
// scrubbing never decodes/uploads (the array is already fully GPU-resident).

import type { Manifest, Allergen } from "./manifest";

// --- Legacy module-level slice geometry (single-region path) ----------------
// These globals remain for the calibration tool and any single-region consumer
// that still reads them as ES live bindings. The MULTI-REGION product path no
// longer depends on them: each region carries its own SliceGeometry (below) so
// regions of DIFFERENT dimensions (EU 600x800, NA 585x260, Asia 440x320) can be
// decoded and rendered simultaneously without clobbering a shared global. See
// regionRegistry.ts + the geometry-parameterised decode functions further down.
export let SLICE_W = 600;
export let SLICE_H = 800;
export let N_SLICES = 36;
export let SLICE_BYTES = SLICE_W * SLICE_H; // SLICE_W*SLICE_H
let SLICES_PER_STRIP = 12;

/** Per-region slice geometry — the multi-region replacement for the globals. */
export interface SliceGeometry {
  width: number; // per-slice width  (px)
  height: number; // per-slice height (px)
  nSlices: number; // weekly slice count (52)
  slicesPerStrip: number; // slices stacked vertically per PNG strip (13)
  sliceBytes: number; // width*height
}

/** Derive a SliceGeometry from a manifest (does NOT mutate the globals). */
export function geometryFromManifest(manifest: Manifest): SliceGeometry {
  const enc = manifest.encoding;
  const nSlices = manifest.timeSlices?.length ?? manifest.nTimeSlices ?? 52;
  const slicesPerStrip =
    enc?.slicesPerStrip && enc.slicesPerStrip > 0 ? enc.slicesPerStrip : 13;
  const width = manifest.dims?.width ?? enc?.sliceWidth ?? 600;
  const height = manifest.dims?.height ?? enc?.sliceHeight ?? 800;
  return { width, height, nSlices, slicesPerStrip, sliceBytes: width * height };
}

/**
 * Configure the LEGACY slice geometry globals from the loaded manifest. Kept for
 * the calibration tool / single-region path. The multi-region product path uses
 * SliceGeometry instances instead (see geometryFromManifest). Called once from
 * loadManifest().
 */
export function setSliceLayout(manifest: Manifest): void {
  const g = geometryFromManifest(manifest);
  if (g.nSlices > 0) N_SLICES = g.nSlices;
  if (g.slicesPerStrip > 0) SLICES_PER_STRIP = g.slicesPerStrip;
  if (g.width > 0) SLICE_W = g.width;
  if (g.height > 0) SLICE_H = g.height;
  SLICE_BYTES = SLICE_W * SLICE_H;
}

// A 2d context for reading the RED channel out of a decoded ImageBitmap. (R8
// PNG-L decodes to RGBA where r=g=b=value; we keep r.)
//
// CONCURRENCY NOTE (regression fix): the multi-region loader decodes the SAME
// taxon in every region CONCURRENTLY (RegionRegistry.ensureCodeDecoded /
// loadMasks both use Promise.all), and the three regions have DIFFERENT slice
// dimensions (EU 600x800, NA 585x260, Asia 440x320). A single shared, on-demand
// *resized* scratch canvas was therefore a data race: while one region's decode
// was suspended at an `await`, another region resized (and cleared) the shared
// canvas, so the suspended decode's getImageData read a now-wrong-sized canvas
// and returned zeros for the rows outside the other region's smaller bounds —
// partially zeroing the decoded grid (continental Europe rows wiped). We now
// allocate a LOCAL, per-call canvas sized to that call's geometry (exactly as
// decodeMaskHires already does), so each decode owns its canvas and no other
// in-flight decode can resize/clear it. Never reintroduce a shared canvas here.
function makeScratch(
  w: number,
  h: number,
): {
  canvas: OffscreenCanvas | HTMLCanvasElement;
  ctx: OffscreenCanvasRenderingContext2D | CanvasRenderingContext2D;
} {
  let canvas: OffscreenCanvas | HTMLCanvasElement;
  if (typeof OffscreenCanvas !== "undefined") {
    canvas = new OffscreenCanvas(w, h);
  } else {
    const c = document.createElement("canvas");
    c.width = w;
    c.height = h;
    canvas = c;
  }
  const ctx = canvas.getContext("2d", {
    willReadFrequently: true,
  }) as OffscreenCanvasRenderingContext2D | CanvasRenderingContext2D;
  return { canvas, ctx };
}

/**
 * Decode one allergen's N_SLICES slices into a packed Uint8Array (layer-major).
 * Returns N_SLICES*SLICE_BYTES bytes; byte at slice*SLICE_BYTES + y*SLICE_W + x
 * is the raw level byte (0,64,128,192,255) for that source pixel (row 0 = TOP).
 * N_SLICES / SLICES_PER_STRIP are configured from the manifest (setSliceLayout).
 */
export async function decodeAllergen(
  allergen: Allergen,
  base = "./data/",
): Promise<Uint8Array> {
  return decodeAllergenGeo(allergen, base, {
    width: SLICE_W,
    height: SLICE_H,
    nSlices: N_SLICES,
    slicesPerStrip: SLICES_PER_STRIP,
    sliceBytes: SLICE_BYTES,
  });
}

/**
 * Geometry-aware allergen decode (multi-region path). Identical to
 * decodeAllergen but reads the slice dimensions / count / strip layout from the
 * passed SliceGeometry instead of the module globals, so a region of any size
 * (EU 600x800, NA 585x260, Asia 440x320) decodes correctly even while another
 * region's decode is in flight.
 */
export async function decodeAllergenGeo(
  allergen: Allergen,
  base: string,
  g: SliceGeometry,
): Promise<Uint8Array> {
  const out = new Uint8Array(g.nSlices * g.sliceBytes);
  // Local, per-call canvas (see makeScratch concurrency note): owning our own
  // canvas makes concurrent multi-region decodes geometry-stable.
  const { ctx } = makeScratch(g.width, g.height);

  for (let s = 0; s < allergen.files.length; s++) {
    const url = `${base}${allergen.files[s]}`;
    const resp = await fetch(url);
    if (!resp.ok) throw new Error(`${url} -> HTTP ${resp.status}`);
    const blob = await resp.blob();

    for (let j = 0; j < g.slicesPerStrip; j++) {
      const globalSlice = s * g.slicesPerStrip + j;
      if (globalSlice >= g.nSlices) break;
      const bitmap = await createImageBitmap(
        blob,
        0,
        j * g.height,
        g.width,
        g.height,
        { colorSpaceConversion: "none", premultiplyAlpha: "none" },
      );
      ctx.clearRect(0, 0, g.width, g.height);
      ctx.drawImage(bitmap, 0, 0);
      bitmap.close();
      const img = ctx.getImageData(0, 0, g.width, g.height).data; // RGBA
      const off = globalSlice * g.sliceBytes;
      for (let p = 0; p < g.sliceBytes; p++) {
        out[off + p] = img[p * 4]; // RED channel
      }
    }
  }
  return out;
}

/**
 * Decode the shared 600x800 no-data/ocean mask into a Uint8Array (byte 255 =
 * transparent ocean/no-data, byte 0 = data). Row 0 = TOP.
 */
export async function decodeMask(
  manifest: Manifest,
  base = "./data/",
): Promise<Uint8Array> {
  return decodeMaskGeo(manifest, base, SLICE_W, SLICE_H);
}

/**
 * Geometry-aware low-res mask decode (multi-region path). Decodes the
 * data-grid-sized no-data/ocean mask at the passed (w,h); byte 255 = transparent
 * ocean/no-data, byte 0 = data. Row 0 = TOP.
 */
export async function decodeMaskGeo(
  manifest: Manifest,
  base: string,
  w: number,
  h: number,
): Promise<Uint8Array> {
  const url = `${base}${manifest.noData.maskFile}`;
  const resp = await fetch(url);
  if (!resp.ok) throw new Error(`${url} -> HTTP ${resp.status}`);
  const blob = await resp.blob();
  const bitmap = await createImageBitmap(blob, {
    colorSpaceConversion: "none",
    premultiplyAlpha: "none",
  });
  // Local, per-call canvas (see makeScratch concurrency note) — decodeMaskGeo
  // also runs under the concurrent loadMasks Promise.all.
  const { ctx } = makeScratch(w, h);
  ctx.clearRect(0, 0, w, h);
  ctx.drawImage(bitmap, 0, 0);
  bitmap.close();
  const img = ctx.getImageData(0, 0, w, h).data;
  const out = new Uint8Array(w * h);
  for (let p = 0; p < out.length; p++) out[p] = img[p * 4];
  return out;
}

export interface DecodedHiresMask {
  data: Uint8Array; // width*height RED-channel bytes (0 = data, 255 = no-data)
  width: number;
  height: number;
}

/**
 * Decode the hi-res transparency/land mask (C1 fix) at its own native size
 * (e.g. 3600x4800). Same polarity as the 600x800 mask (byte 255 = transparent
 * ocean/no-data, byte 0 = data). Allocated on a dedicated canvas sized to the
 * mask so the small per-slice scratch canvas is not clobbered. Row 0 = TOP.
 */
export async function decodeMaskHires(
  manifest: Manifest,
  base = "./data/",
): Promise<DecodedHiresMask> {
  const file = manifest.noData.maskFileHires;
  if (!file) throw new Error("manifest.noData.maskFileHires is missing");
  const url = `${base}${file}`;
  const resp = await fetch(url);
  if (!resp.ok) throw new Error(`${url} -> HTTP ${resp.status}`);
  const blob = await resp.blob();
  const bitmap = await createImageBitmap(blob, {
    colorSpaceConversion: "none",
    premultiplyAlpha: "none",
  });
  const width = manifest.noData.maskHiresWidth ?? bitmap.width;
  const height = manifest.noData.maskHiresHeight ?? bitmap.height;

  let canvas: OffscreenCanvas | HTMLCanvasElement;
  if (typeof OffscreenCanvas !== "undefined") {
    canvas = new OffscreenCanvas(width, height);
  } else {
    const c = document.createElement("canvas");
    c.width = width;
    c.height = height;
    canvas = c;
  }
  const ctx = canvas.getContext("2d", {
    willReadFrequently: true,
  }) as
    | OffscreenCanvasRenderingContext2D
    | CanvasRenderingContext2D;
  ctx.clearRect(0, 0, width, height);
  ctx.drawImage(bitmap, 0, 0);
  bitmap.close();
  const img = ctx.getImageData(0, 0, width, height).data;
  const out = new Uint8Array(width * height);
  for (let p = 0; p < out.length; p++) out[p] = img[p * 4];
  return { data: out, width, height };
}
